import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const LangToggle = () => {
  const { lang, setLang } = useI18n();
  const langs = ["en", "de", "ro"];
  return (
    <div
      className="flex items-center gap-0 border border-[#3A3E43] font-mono text-[11px] uppercase tracking-[0.18em]"
      data-testid="lang-toggle"
    >
      {langs.map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-btn-${l}`}
          className={`px-2.5 py-1.5 transition-colors duration-200 ${
            lang === l
              ? "bg-[#C2674A] text-black"
              : "text-[#A0A0A0] hover:text-white hover:bg-[#2E3136]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Header = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { id: "problem", label: t.nav.problem },
    { id: "solution", label: t.nav.solution },
    { id: "design", label: t.nav.design },
    { id: "how", label: t.nav.howItWorks },
    { id: "features", label: t.nav.features },
    { id: "market", label: t.nav.market },
    { id: "team", label: t.nav.team },
  ];

  const goTo = (id) => {
    setOpen(false);
    if (!isLanding) {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "border-[#3A3E43] bg-black/70 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link
          to="/"
          className="flex items-center gap-2 group"
          data-testid="logo-link"
        >
          <div className="w-7 h-7 bg-[#C2674A] flex items-center justify-center">
            <span className="text-black font-heading font-black text-sm">L</span>
          </div>
          <span className="font-heading font-black text-lg tracking-[0.3em] text-white">
            LìBER
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((n) => (
            <button
              key={n.id}
              onClick={() => goTo(n.id)}
              data-testid={`nav-${n.id}`}
              className="text-sm text-[#A0A0A0] hover:text-white transition-colors duration-200 font-medium"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle />
          <Link
to="/platform"
            data-testid="header-platform-link"
            className="hidden md:inline-flex items-center px-4 py-2 bg-[#C2674A] text-black hover:bg-[#A8553A] transition-colors duration-200 text-sm font-semibold tracking-tight"
          >
            {t.nav.platform}
          </Link>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2 -mr-2"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#3A3E43] bg-black/90 backdrop-blur-xl">
          <div className="max-w-[1500px] mx-auto px-4 py-4 flex flex-col gap-1">
            {navItems.map((n) => (
              <button
                key={n.id}
                onClick={() => goTo(n.id)}
                data-testid={`mobile-nav-${n.id}`}
                className="text-left py-3 px-2 text-[#A0A0A0] hover:text-white hover:bg-[#2E3136] transition-colors text-sm font-medium"
              >
                {n.label}
              </button>
            ))}
            <Link
to="/platform"
              onClick={() => setOpen(false)}
              data-testid="mobile-platform-link"
              className="mt-2 text-center px-4 py-3 bg-[#C2674A] text-black text-sm font-semibold"
            >
              {t.nav.platform}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
